import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import Joi from 'joi';

import {
    useUpdateShareSubcategoryMutation,
    useCreateShareSubcategoryMutation,
    useGetDocumentQuery,
} from 'api/main';
import {
    getFileExtension,
    parseBackendViolations,
    validateFileSize,
    validateSchema,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomTextField, Loader, CheckboxLabel } from 'components';
import { Box, InputAdornment } from '@mui/material';
import * as icons from 'mdi-material-ui';
import FundFile from '../FundFile';

const initErrors = () => ({
    name: '',
    value: '',
    nominalValue: '',
    admissionFee: '',
    managementFee: '',
    exitFee: '',
    performanceFee: '',
    maxDistributorAdmissionFee: '',
    distributorManagementFee: '',
    subscriptionFee: '',
    minInvestedAmount: '',
    maxInvestedAmount: '',
    isinCode: '',
});
const initForm = () => ({
    ...initErrors(),
    dici: null,
    creditTransfer: false,
    directDebit: false,
    immediatePayment: false,
    accessibleToDistribution: false,
    reserved: false,
});

const getPercentageSchema = () =>
    Joi.number().min(0).max(100).required().messages({
        'number.base': texts.SAISIR_LE_CHAMP,
        'number.min': texts.INCORRECT_INPUT,
        'number.max': texts.INCORRECT_INPUT,
    });

const schema = Joi.object({
    name: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    value: Joi.number().greater(0).required().messages({
        'number.base': texts.SAISIR_LE_CHAMP,
        'number.greater': texts.SAISIR_LE_CHAMP,
    }),
    nominalValue: Joi.number().greater(0).required().messages({
        'number.base': texts.SAISIR_LE_CHAMP,
        'number.greater': texts.INVALID_INPUT,
    }),
    admissionFee: getPercentageSchema(),
    managementFee: getPercentageSchema(),
    exitFee: getPercentageSchema(),
    performanceFee: getPercentageSchema(),
    creditTransfer: Joi.bool()
        .required()
        .when('directDebit', {
            is: false,
            then: Joi.valid(true).messages({
                'any.only': texts.CHOOSE_PAYMENT_MODE,
            }),
        }),
    directDebit: Joi.any(),
    immediatePayment: Joi.any(),
    maxDistributorAdmissionFee: Joi.number()
        .min(0)
        .max(100)
        .required()
        .custom((value, helpers) => {
            const { admissionFee } = helpers.state.ancestors[0];

            if (admissionFee !== 0 && value > admissionFee) {
                return helpers.error('number.less');
            }

            return value; // valid
        }, 'Custom maxDistributorAdmissionFee validation')
        .messages({
            'number.base': texts.SAISIR_LE_CHAMP,
            'number.min': texts.INCORRECT_INPUT,
            'number.max': texts.INCORRECT_INPUT,
            'number.less': texts.MAX_DISTRIBUTOR_ADMISSION_FEE_ERROR,
        }),
    distributorManagementFee: getPercentageSchema(),
    subscriptionFee: getPercentageSchema(),
    minInvestedAmount: Joi.number().min(0).required().messages({
        'number.base': texts.SAISIR_LE_CHAMP,
        'number.min': texts.INCORRECT_INPUT,
    }),
    maxInvestedAmount: Joi.number()
        .min(0)
        .allow('')
        .when('minInvestedAmount', {
            is: Joi.number().exist(),
            then: Joi.number().min(Joi.ref('minInvestedAmount')).messages({
                'number.min': texts.INCORRECT_INPUT,
            }),
            otherwise: Joi.number().min(0),
        })
        .messages({
            'number.base': texts.SAISIR_LE_CHAMP,
            'number.min': texts.INCORRECT_INPUT,
        }),
    accessibleToDistribution: Joi.any(),
    isinCode: Joi.string().alphanum().required().messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.alphanum': texts.SAISIR_LE_CHAMP,
    }),
    reserved: Joi.any(),
});

const SubcategoryForm = ({ onCancel, subcategory, categoryId, onSave, hasSubscriptions }) => {
    const uId = useRef(subcategory?.id || uniqueId());
    const [
        updateSubcategory,
        {
            isLoading: updateLoading,
            isSuccess: updateSuccess,
            error: updateError,
            isError: isUpdateError,
        },
    ] = useUpdateShareSubcategoryMutation();
    const [
        createSubcategory,
        {
            isLoading: createLoading,
            isSuccess: createSuccess,
            error: createError,
            isError: isCreateError,
        },
    ] = useCreateShareSubcategoryMutation();
    const { data: diciDoc, isLoading: diciLoading } = useGetDocumentQuery(
        subcategory?.dici?.split?.('/')?.pop(),
        { skip: !subcategory?.dici }
    );

    const [form, setForm] = useState(subcategory || initForm());
    const [errors, setErrors] = useState(initErrors());
    const [fileSizeErrors, setFileSizeErrors] = useState({ dici: '' });
    const [fileTypeErrors, setFileTypeErrors] = useState({ dici: '' });

    // Handle success side effects
    useEffect(() => {
        if (updateSuccess || createSuccess) {
            onCancel(); // Close the form on success
        }
    }, [updateSuccess, createSuccess, onCancel]);

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
            setErrors(prevState => ({ ...prevState, [key]: '' }));
        },
        []
    );
    const handleCheckboxChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.checked }));
        },
        []
    );
    const handleFileChange = useCallback(
        key => e => {
            const file = e.target.files[0];
            const fileExtension = getFileExtension(file);

            if (!validateFileSize(file)) {
                // set file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: texts.FILE_TOO_LARGE }));
                return;
            } else {
                // clear file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: '' }));
            }

            if (fileExtension !== 'pdf') {
                setFileTypeErrors(prevState => ({
                    ...prevState,
                    [key]: texts.FILE_TYPE_INCORRECT,
                }));
            } else {
                setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
                setForm(prevState => ({ ...prevState, [key]: file }));
            }
        },
        []
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form, { convert: true });
        if (valid) {
            setErrors(initErrors());

            const payload = {
                name: form.name.trim(),
                value: parseFloat(form.value),
                nominalValue: parseFloat(form.nominalValue),
                admissionFee: parseFloat(form.admissionFee),
                managementFee: parseFloat(form.managementFee),
                exitFee: parseFloat(form.exitFee),
                performanceFee: parseFloat(form.performanceFee),
                creditTransfer: form.creditTransfer,
                directDebit: form.directDebit,
                immediatePayment: form.immediatePayment,
                maxDistributorAdmissionFee: parseFloat(form.maxDistributorAdmissionFee),
                distributorManagementFee: parseFloat(form.distributorManagementFee),
                subscriptionFee: parseFloat(form.subscriptionFee),
                minInvestedAmount: parseFloat(form.minInvestedAmount),
                maxInvestedAmount: parseFloat(form.maxInvestedAmount),
                accessibleToDistribution: form.accessibleToDistribution,
                dici: form.dici,
                isinCode: form.isinCode.trim(),
                reserved: form.reserved,
            };

            // Case 1: Creating a new category (categoryId is null/undefined and onSave is provided)
            if (!categoryId && onSave) {
                // For new categories, add subcategory to the category form's shareSubcategory list
                onSave(payload);
            }
            // Case 2: Editing an existing category (categoryId exists)
            else if (categoryId) {
                if (subcategory?.id) {
                    // Update existing subcategory in DB
                    updateSubcategory({
                        id: subcategory.id,
                        ...payload,
                        diciId: diciDoc?.id,
                    });
                } else {
                    // Create new subcategory in DB for existing category
                    const createPayload = {
                        ...payload,
                        shareCategory: `/api/share_categories/${categoryId}`,
                    };
                    createSubcategory(createPayload);
                }
            }
        } else {
            setErrors(newErrors);
        }
    }, [form, subcategory, onSave, updateSubcategory, categoryId, createSubcategory, diciDoc]);

    useEffect(() => {
        if (isUpdateError || isCreateError) {
            const violations = parseBackendViolations({
                ...updateError?.data,
                ...createError?.data,
            });
            if (violations) {
                setErrors(prev => ({ ...prev, ...violations }));
            }
        }
    }, [isUpdateError, isCreateError, updateError, createError]);

    useEffect(() => {
        if (diciDoc) {
            setForm(prevState => ({ ...prevState, dici: diciDoc }));
        }
    }, [diciDoc]);

    return (
        <React.Fragment>
            <Box display="flex" flexDirection="column">
                <CustomTextField
                    fullWidth
                    label={texts.SUBCATEGORY_NAME}
                    onChange={handleChange('name')}
                    value={form.name}
                    error={texts.SHARE_SUBCATEGORY_EXIST(errors.name)}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.SHARE_VALUE}
                    onChange={handleChange('value')}
                    value={form.value}
                    error={errors.value}
                    endAdornment={<InputAdornment position="start">€</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    disabled={hasSubscriptions}
                    label={texts.NOMINAL_SHARE_VALUE}
                    onChange={handleChange('nominalValue')}
                    value={form.nominalValue}
                    error={errors.nominalValue}
                    endAdornment={<InputAdornment position="start">€</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.ADMISSION_FEE}
                    onChange={handleChange('admissionFee')}
                    value={form.admissionFee}
                    error={errors.admissionFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.MANAGEMENT_FEE}
                    onChange={handleChange('managementFee')}
                    value={form.managementFee}
                    error={errors.managementFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.EXIT_FEE}
                    onChange={handleChange('exitFee')}
                    value={form.exitFee}
                    error={errors.exitFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.PERFORMANCE_FEE}
                    onChange={handleChange('performanceFee')}
                    value={form.performanceFee}
                    error={errors.performanceFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CheckboxLabel
                    sx={{ alignItems: 'center' }}
                    label={texts.CREDIT_TRANSFER}
                    checked={form.creditTransfer}
                    onChange={handleCheckboxChange('creditTransfer')}
                />
                <CheckboxLabel
                    sx={{ alignItems: 'center', mb: errors.creditTransfer ? 0 : 2 }}
                    label={texts.DEBIT_TRANSFER}
                    checked={form.directDebit}
                    error={errors.creditTransfer} // the error message takes into account both payment methods
                    onChange={handleCheckboxChange('directDebit')}
                />
                <CheckboxLabel
                    sx={{ alignItems: 'center' }}
                    label={texts.IMMEDIATE_PAYMENT}
                    checked={form.immediatePayment}
                    onChange={handleCheckboxChange('immediatePayment')}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.MAX_DISTRIBUTOR_ADMISSION_FEE}
                    onChange={handleChange('maxDistributorAdmissionFee')}
                    value={form.maxDistributorAdmissionFee}
                    error={errors.maxDistributorAdmissionFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.DISTRIBUTOR_MANAGEMENT_FEE}
                    onChange={handleChange('distributorManagementFee')}
                    value={form.distributorManagementFee}
                    error={errors.distributorManagementFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.SUBSCRIPTION_FEE}
                    onChange={handleChange('subscriptionFee')}
                    value={form.subscriptionFee}
                    error={errors.subscriptionFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.MIN_INVESTED_AMOUNT}
                    onChange={handleChange('minInvestedAmount')}
                    value={form.minInvestedAmount}
                    error={errors.minInvestedAmount}
                    endAdornment={<InputAdornment position="start">€</InputAdornment>}
                />
                <CustomTextField
                    fullWidth
                    type="number"
                    label={texts.MAX_INVESTED_AMOUNT}
                    onChange={handleChange('maxInvestedAmount')}
                    value={form.maxInvestedAmount}
                    error={errors.maxInvestedAmount}
                    endAdornment={<InputAdornment position="start">€</InputAdornment>}
                />
                <CheckboxLabel
                    sx={{ alignItems: 'center' }}
                    label={texts.ACCESSIBLE_TO_DISTRIBUTION}
                    checked={form.accessibleToDistribution}
                    onChange={handleCheckboxChange('accessibleToDistribution')}
                />
                <Loader loading={diciLoading} marginTop={0}>
                    <FundFile
                        id={`dici-${uId.current}`}
                        acceptedFileTypes=".pdf"
                        containerSx={{ width: '100%' }}
                        onChange={handleFileChange('dici')}
                        name={form.dici?.name || texts.DICI_LABEL}
                        label={texts.DICI_LABEL}
                        typeError={!!fileTypeErrors.dici}
                        sizeError={!!fileSizeErrors.dici}
                        file={form.dici}
                    />
                </Loader>
                <CustomTextField
                    fullWidth
                    label={texts.ISIN_CODE}
                    onChange={handleChange('isinCode')}
                    value={form.isinCode}
                    error={errors.isinCode}
                />
                <CheckboxLabel
                    sx={{ alignItems: 'center' }}
                    label={texts.RESERVED}
                    checked={form.reserved}
                    onChange={handleCheckboxChange('reserved')}
                />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <icons.CloseThick
                    sx={{ cursor: 'pointer', '&:hover': { color: 'error.main' } }}
                    onClick={onCancel}
                />
                <Loader loading={updateLoading || createLoading} size={20} marginTop={0}>
                    <icons.CheckBold
                        sx={{ cursor: 'pointer', '&:hover': { color: 'success.main' } }}
                        onClick={handleSubmit}
                    />
                </Loader>
            </Box>
        </React.Fragment>
    );
};

SubcategoryForm.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    subcategory: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        value: PropTypes.number,
        nominalValue: PropTypes.number,
        admissionFee: PropTypes.number,
        managementFee: PropTypes.number,
        exitFee: PropTypes.number,
        performanceFee: PropTypes.number,
        creditTransfer: PropTypes.bool,
        directDebit: PropTypes.bool,
        immediatePayment: PropTypes.bool,
        maxDistributorAdmissionFee: PropTypes.number,
        distributorManagementFee: PropTypes.number,
        subscriptionFee: PropTypes.number,
        minInvestedAmount: PropTypes.number,
        maxInvestedAmount: PropTypes.number,
        accessibleToDistribution: PropTypes.bool,
        isinCode: PropTypes.string,
        reserved: PropTypes.bool,
        dici: PropTypes.any,
    }),
    categoryId: PropTypes.number,
    hasSubscriptions: PropTypes.bool,
};

export default SubcategoryForm;
